import React, { useMemo, useState } from 'react';
import { injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import {
    Checkbox,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';

import ChartCard from '~/components/ChartCard';
import InfoCard from '~/components/InfoCard';
import LocaleMessage from '~/components/LocaleMessage';

import GetPluginspaceTheme from '~/util/PluginspaceTheme';

import { ChatCardContainer } from '../../Dashboard/styles';
import {
    BATTERY_MODE_ALL,
    buildBatteryByMap,
    buildBatteryByPeriod,
    buildBatteryByRobot,
    buildBatteryKpis,
    groupTopMapsAndOthers,
} from './cleaningReportBatteryMetrics';
import { getCleaningReportChartColors } from './cleaningReportUtils';

function formatPct(value) {
    if (value == null || !Number.isFinite(Number(value))) return '—';
    const n = Number(value);
    return `${Number.isInteger(n) ? n : n.toFixed(1)}%`;
}

function BatteryBarTooltip({ active, payload, intl }) {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0].payload || {};
    const missionsSuffix =
        intl && typeof intl.formatMessage === 'function'
            ? intl
                  .formatMessage({ id: 'dashboard.card.missions' })
                  .toLowerCase()
            : 'missões';
    return (
        <div className="jr-card p-2 mb-0">
            <div>
                <strong>{row.name || row.date}</strong>
            </div>
            <div className="jr-fs-13 jr-font-weight-medium text-primary">
                {formatPct(row.avg)}
            </div>
            {row.min != null && row.max != null ? (
                <div className="text-muted" style={{ fontSize: 11 }}>
                    Min: {formatPct(row.min)} · Max: {formatPct(row.max)}
                </div>
            ) : null}
            {row.tasks != null ? (
                <div className="text-muted" style={{ fontSize: 11 }}>
                    {row.tasks} {missionsSuffix}
                </div>
            ) : null}
        </div>
    );
}

BatteryBarTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.arrayOf(PropTypes.shape({})),
    intl: PropTypes.shape({
        formatMessage: PropTypes.func,
    }),
};

BatteryBarTooltip.defaultProps = {
    active: false,
    payload: [],
    intl: null,
};

function CleaningReportBatteryDashboard({
    list,
    localeCode: _localeCode,
    periodDates,
    intl,
}) {
    const theme = useTheme();
    const colors = GetPluginspaceTheme(theme);
    const {
        graphColor,
        cardColor,
        pieSegmentColors,
    } = getCleaningReportChartColors(colors);
    const settings = useSelector(state => state.settings || null);
    const darkMode = !!(settings && settings.dark_mode);
    const tickFill = darkMode ? '#bdbdbd' : '#616161';
    const gridStroke = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';

    const modeFilter = BATTERY_MODE_ALL;

    const unknownMap = intl.formatMessage({
        id: 'page.robots.cleaning_report.battery.unknown_map',
    });
    const othersLabel = intl.formatMessage({
        id: 'page.robots.cleaning_report.battery.others_maps',
    });

    const kpis = useMemo(() => buildBatteryKpis(list, modeFilter), [
        list,
        modeFilter,
    ]);
    const allMaps = useMemo(
        () =>
            buildBatteryByMap(list, {
                modeFilter,
                unknownLabel: unknownMap,
                othersLabel,
                limit: null,
            }),
        [list, modeFilter, unknownMap, othersLabel]
    );

    const [selectedMapKeys, setSelectedMapKeys] = useState(null);

    const allMapKeys = useMemo(() => allMaps.map(m => m.key), [allMaps]);

    const isAllSelected =
        selectedMapKeys === null ||
        selectedMapKeys.length === allMapKeys.length;
    const isNoneSelected =
        selectedMapKeys !== null && selectedMapKeys.length === 0;

    const displayedMapData = useMemo(() => {
        if (!allMaps.length) return [];
        if (isNoneSelected) return [];
        const base = isAllSelected
            ? allMaps
            : allMaps.filter(m => selectedMapKeys.includes(m.key));
        return groupTopMapsAndOthers(base, 5, othersLabel);
    }, [allMaps, isAllSelected, isNoneSelected, selectedMapKeys, othersLabel]);

    const handleMapSelectionChange = event => {
        const val = event.target.value;
        if (val.includes('__select_all__')) {
            if (isAllSelected) {
                setSelectedMapKeys([]);
            } else {
                setSelectedMapKeys(allMapKeys);
            }
            return;
        }
        setSelectedMapKeys(val);
    };

    const mapChartHeight = Math.max(260, displayedMapData.length * 36 + 40);

    const byRobot = useMemo(() => buildBatteryByRobot(list, modeFilter), [
        list,
        modeFilter,
    ]);
    const byPeriod = useMemo(
        () => buildBatteryByPeriod(list, { modeFilter, periodDates }),
        [list, modeFilter, periodDates]
    );

    const hasAnyBattery = buildBatteryKpis(list, BATTERY_MODE_ALL).count > 0;

    if (!Array.isArray(list) || list.length === 0) {
        return null;
    }

    const chartHeight = 260;

    const renderWaiting = () => (
        <div className="row card-row mb-5">
            <div className="col-12 dash-card">
                <ChatCardContainer>
                    <ChartCard
                        title={
                            <LocaleMessage msg="page.robots.cleaning_report.battery.title" />
                        }
                        value=""
                        desc={
                            <LocaleMessage msg="page.robots.cleaning_report.battery.waiting_details" />
                        }
                    />
                </ChatCardContainer>
            </div>
        </div>
    );

    if (!hasAnyBattery) {
        return renderWaiting();
    }

    return (
        <div>
            <div className="row card-row mb-3">
                <div className="col-md-3 col-sm-6 col-12 dash-card mb-3">
                    <InfoCard
                        data={{
                            title: formatPct(kpis.avg),
                            subTitle: (
                                <LocaleMessage msg="page.robots.cleaning_report.battery.kpi.avg" />
                            ),
                        }}
                        color={cardColor}
                        icon="battery"
                    />
                </div>
                <div className="col-md-3 col-sm-6 col-12 dash-card mb-3">
                    <InfoCard
                        data={{
                            title: formatPct(kpis.min),
                            subTitle: (
                                <LocaleMessage msg="page.robots.cleaning_report.battery.kpi.min" />
                            ),
                        }}
                        color={cardColor}
                        icon="long-arrow-down"
                    />
                </div>
                <div className="col-md-3 col-sm-6 col-12 dash-card mb-3">
                    <InfoCard
                        data={{
                            title: formatPct(kpis.max),
                            subTitle: (
                                <LocaleMessage msg="page.robots.cleaning_report.battery.kpi.max" />
                            ),
                        }}
                        color={cardColor}
                        icon="long-arrow-up"
                    />
                </div>
                <div className="col-md-3 col-sm-6 col-12 dash-card mb-3">
                    <InfoCard
                        data={{
                            title:
                                kpis.efficiency != null
                                    ? `${formatPct(kpis.efficiency)} / 100 m²`
                                    : '—',
                            subTitle: (
                                <LocaleMessage msg="page.robots.cleaning_report.battery.kpi.efficiency" />
                            ),
                        }}
                        color={graphColor}
                        icon="flash"
                    />
                </div>
            </div>

            <div className="row card-row">
                <div className="col-12 mb-5 dash-card">
                    <ChatCardContainer>
                        <ChartCard
                            title={
                                <LocaleMessage msg="page.robots.cleaning_report.battery.by_map.title" />
                            }
                            value=""
                            desc={
                                <LocaleMessage msg="page.robots.cleaning_report.battery.by_map.desc" />
                            }
                        >
                            <div className="d-flex justify-content-end px-3 mb-3">
                                <FormControl
                                    variant="outlined"
                                    size="small"
                                    style={{
                                        minWidth: 240,
                                        maxWidth: 360,
                                        width: '100%',
                                    }}
                                >
                                    <InputLabel id="battery-by-map-select-label">
                                        <LocaleMessage msg="page.robots.cleaning_report.battery.filter_maps" />
                                    </InputLabel>
                                    <Select
                                        labelId="battery-by-map-select-label"
                                        label={intl.formatMessage({
                                            id:
                                                'page.robots.cleaning_report.battery.filter_maps',
                                        })}
                                        multiple
                                        value={
                                            isAllSelected
                                                ? allMapKeys
                                                : selectedMapKeys || []
                                        }
                                        onChange={handleMapSelectionChange}
                                        renderValue={selected => {
                                            if (
                                                isNoneSelected ||
                                                !selected.length
                                            ) {
                                                return (
                                                    <span className="text-muted">
                                                        <LocaleMessage msg="page.robots.cleaning_report.battery.no_maps_selected" />
                                                    </span>
                                                );
                                            }
                                            if (
                                                selected.length ===
                                                allMapKeys.length
                                            ) {
                                                return (
                                                    <LocaleMessage
                                                        msg="page.robots.cleaning_report.battery.all_maps_selected"
                                                        values={{
                                                            count:
                                                                allMapKeys.length,
                                                        }}
                                                    />
                                                );
                                            }
                                            return (
                                                <LocaleMessage
                                                    msg="page.robots.cleaning_report.battery.selected_maps_count"
                                                    values={{
                                                        count: selected.length,
                                                    }}
                                                />
                                            );
                                        }}
                                        MenuProps={{
                                            getContentAnchorEl: null,
                                            anchorOrigin: {
                                                vertical: 'bottom',
                                                horizontal: 'left',
                                            },
                                            transformOrigin: {
                                                vertical: 'top',
                                                horizontal: 'left',
                                            },
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 340,
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem
                                            value="__select_all__"
                                            dense
                                            style={{
                                                fontWeight: 600,
                                                borderBottom:
                                                    '1px solid rgba(0,0,0,0.08)',
                                            }}
                                        >
                                            <Checkbox
                                                color="primary"
                                                size="small"
                                                checked={isAllSelected}
                                                indeterminate={
                                                    !isAllSelected &&
                                                    !isNoneSelected &&
                                                    (selectedMapKeys || [])
                                                        .length > 0
                                                }
                                                style={{
                                                    marginRight: 8,
                                                    padding: 4,
                                                }}
                                            />
                                            <ListItemText
                                                primary={
                                                    <LocaleMessage
                                                        msg={
                                                            isAllSelected
                                                                ? 'label.deselect_all'
                                                                : 'label.select_all'
                                                        }
                                                    />
                                                }
                                            />
                                        </MenuItem>
                                        {allMaps.map(m => {
                                            const isChecked = isAllSelected
                                                ? true
                                                : (
                                                      selectedMapKeys || []
                                                  ).includes(m.key);
                                            return (
                                                <MenuItem
                                                    key={m.key}
                                                    value={m.key}
                                                    dense
                                                >
                                                    <Checkbox
                                                        color="primary"
                                                        size="small"
                                                        checked={isChecked}
                                                        style={{
                                                            marginRight: 8,
                                                            padding: 4,
                                                        }}
                                                    />
                                                    <ListItemText
                                                        primary={m.name}
                                                        secondary={`${formatPct(
                                                            m.avg
                                                        )}${
                                                            m.tasks
                                                                ? ` · ${
                                                                      m.tasks
                                                                  } ${intl
                                                                      .formatMessage(
                                                                          {
                                                                              id:
                                                                                  'dashboard.card.missions',
                                                                          }
                                                                      )
                                                                      .toLowerCase()}`
                                                                : ''
                                                        }`}
                                                    />
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            </div>
                            <div className="row">
                                <div className="col-12 mb-3 area-chart-container">
                                    {displayedMapData.length === 0 ? (
                                        <div
                                            className="d-flex flex-column align-items-center justify-content-center p-5 text-center"
                                            style={{ minHeight: 240 }}
                                        >
                                            <i
                                                className="zmdi zmdi-map text-muted mb-2"
                                                style={{ fontSize: 36 }}
                                            />
                                            <p className="text-muted mb-0">
                                                <LocaleMessage msg="page.robots.cleaning_report.battery.select_maps_prompt" />
                                            </p>
                                        </div>
                                    ) : (
                                        <ResponsiveContainer
                                            width="100%"
                                            height={mapChartHeight}
                                        >
                                            <BarChart
                                                layout="vertical"
                                                data={displayedMapData}
                                                margin={{
                                                    top: 8,
                                                    right: 42,
                                                    left: 8,
                                                    bottom: 8,
                                                }}
                                            >
                                                <CartesianGrid
                                                    horizontal={false}
                                                    stroke={gridStroke}
                                                />
                                                <XAxis
                                                    type="number"
                                                    tick={{
                                                        fontSize: 11,
                                                        fill: tickFill,
                                                    }}
                                                    tickFormatter={v => `${v}%`}
                                                />
                                                <YAxis
                                                    type="category"
                                                    dataKey="name"
                                                    width={240}
                                                    interval={0}
                                                    tick={{
                                                        fontSize: 12,
                                                        fill: tickFill,
                                                    }}
                                                />
                                                <Tooltip
                                                    content={
                                                        <BatteryBarTooltip
                                                            intl={intl}
                                                        />
                                                    }
                                                />
                                                <Bar
                                                    dataKey="avg"
                                                    radius={[0, 6, 6, 0]}
                                                    maxBarSize={26}
                                                >
                                                    {displayedMapData.map(
                                                        (row, idx) => (
                                                            <Cell
                                                                key={row.key}
                                                                fill={
                                                                    pieSegmentColors[
                                                                        idx %
                                                                            pieSegmentColors.length
                                                                    ]
                                                                }
                                                            />
                                                        )
                                                    )}
                                                    <LabelList
                                                        dataKey="avg"
                                                        position="right"
                                                        formatter={v =>
                                                            formatPct(v)
                                                        }
                                                        style={{
                                                            fontSize: 12,
                                                            fill: tickFill,
                                                            fontWeight: 500,
                                                        }}
                                                    />
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    )}
                                </div>
                            </div>
                        </ChartCard>
                    </ChatCardContainer>
                </div>
            </div>

            <div className="row card-row">
                {byRobot.length > 1 ? (
                    <div className="col-md-6 col-12 mb-5 dash-card">
                        <ChatCardContainer>
                            <ChartCard
                                title={
                                    <LocaleMessage msg="page.robots.cleaning_report.battery.by_robot.title" />
                                }
                                value=""
                                desc={
                                    <LocaleMessage msg="page.robots.cleaning_report.battery.by_robot.desc" />
                                }
                            >
                                <div className="row">
                                    <div className="col-12 mb-3 area-chart-container">
                                        <ResponsiveContainer
                                            width="100%"
                                            height={chartHeight}
                                        >
                                            <BarChart
                                                data={byRobot}
                                                margin={{
                                                    top: 24,
                                                    right: 16,
                                                    left: 8,
                                                    bottom: 8,
                                                }}
                                            >
                                                <CartesianGrid
                                                    vertical={false}
                                                    stroke={gridStroke}
                                                />
                                                <XAxis
                                                    dataKey="name"
                                                    tick={{
                                                        fontSize: 11,
                                                        fill: tickFill,
                                                    }}
                                                />
                                                <YAxis
                                                    tick={{
                                                        fontSize: 11,
                                                        fill: tickFill,
                                                    }}
                                                    tickFormatter={v => `${v}%`}
                                                    width={44}
                                                />
                                                <Tooltip
                                                    content={
                                                        <BatteryBarTooltip />
                                                    }
                                                />
                                                <Bar
                                                    dataKey="avg"
                                                    fill={cardColor}
                                                    radius={[4, 4, 0, 0]}
                                                    maxBarSize={56}
                                                >
                                                    <LabelList
                                                        dataKey="avg"
                                                        position="top"
                                                        formatter={v =>
                                                            formatPct(v)
                                                        }
                                                        style={{
                                                            fontSize: 12,
                                                            fill: tickFill,
                                                        }}
                                                    />
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </ChartCard>
                        </ChatCardContainer>
                    </div>
                ) : null}

                <div
                    className={`${
                        byRobot.length > 1 ? 'col-md-6' : 'col-12'
                    } mb-5 dash-card`}
                >
                    <ChatCardContainer>
                        <ChartCard
                            title={
                                <LocaleMessage msg="page.robots.cleaning_report.battery.by_period.title" />
                            }
                            value=""
                            desc={
                                <LocaleMessage msg="page.robots.cleaning_report.battery.by_period.desc" />
                            }
                        >
                            <div className="row">
                                <div className="col-12 mb-3 area-chart-container">
                                    <ResponsiveContainer
                                        width="100%"
                                        height={chartHeight}
                                    >
                                        <LineChart
                                            data={byPeriod.data}
                                            margin={{
                                                top: 16,
                                                right: 16,
                                                left: 8,
                                                bottom: 8,
                                            }}
                                        >
                                            <CartesianGrid
                                                stroke={gridStroke}
                                            />
                                            <XAxis
                                                dataKey="date"
                                                tick={{
                                                    fontSize: 11,
                                                    fill: tickFill,
                                                }}
                                            />
                                            <YAxis
                                                tick={{
                                                    fontSize: 11,
                                                    fill: tickFill,
                                                }}
                                                tickFormatter={v => `${v}%`}
                                                width={44}
                                            />
                                            <Tooltip
                                                content={<BatteryBarTooltip />}
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey="avg"
                                                stroke={graphColor}
                                                strokeWidth={2}
                                                dot={{
                                                    r: 3,
                                                    fill: graphColor,
                                                }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </ChartCard>
                    </ChatCardContainer>
                </div>
            </div>
        </div>
    );
}

CleaningReportBatteryDashboard.propTypes = {
    list: PropTypes.arrayOf(PropTypes.shape({})),
    localeCode: PropTypes.string,
    periodDates: PropTypes.shape({
        startDate: PropTypes.string,
        endDate: PropTypes.string,
    }),
    intl: PropTypes.shape({
        formatMessage: PropTypes.func.isRequired,
    }).isRequired,
};

CleaningReportBatteryDashboard.defaultProps = {
    list: [],
    localeCode: 'pt_BR',
    periodDates: null,
};

export default injectIntl(CleaningReportBatteryDashboard);
